import { Link, useLocation } from 'react-router-dom';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu = ({ isOpen, onClose }: MobileMenuProps) => {
  const location = useLocation();

  const menuItems = [
    { label: 'Hospitals', href: '/hospitals', icon: '🏥' },
    { label: 'Rankings', href: '/hospitals/ranking', icon: '📊' },
    { label: 'Visa Guide', href: '/visa-guide', icon: '✈️' },
    { label: 'Medical Visa', href: '/visa/medical', icon: '🏥' },
    { label: 'Guides', href: '/guides', icon: '📚' },
    { label: 'FAQ', href: '/faq', icon: '❓' },
    { label: 'Services', href: '/services', icon: '💼' },
    { label: 'Contact', href: '/contact', icon: '💬' },
  ];

  const handleLinkClick = () => {
    onClose();
  };

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-label="Close menu"
      />



      {/* Mobile Menu Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Main navigation menu"
        className={`
          fixed inset-0 z-50 overflow-hidden transition-transform duration-300 md:hidden
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm" onClick={onClose} />

        {/* Menu Content */}
        <div className="absolute inset-0 bg-white max-w-sm w-full h-full overflow-y-auto">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Menu</h2>
            <button
              onClick={onClose}
              className="min-h-[44px] min-w-[44px] p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="px-4 py-6 space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors min-h-[44px]"
                aria-current={isActive(item.href) ? 'page' : undefined}
              >
                <span className="text-xl" role="presentation" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="flex-grow font-medium">
                  {item.label}
                </span>
                 {isActive(item.href) && (
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4H4a1 1 0 00-2 2 2 2 2h2.83a1 1 0 00-2 2 2h2.83a1 1 0 011 2 2.83a1 1 0 002 2.83z" />
                  </svg>
                )}
              </Link>
            ))}
          </nav>

          {/* Footer Quick Links */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600 mb-3">Quick Links</p>
            <div className="space-y-2">
              <Link
                to="/services"
                onClick={handleLinkClick}
                className="block px-4 py-3 min-h-[44px] text-gray-700 hover:bg-white hover:shadow-sm rounded-lg transition-all"
              >
                💼 Services & Pricing
              </Link>
              <Link
                to="/contact"
                onClick={handleLinkClick}
                className="block px-4 py-3 min-h-[44px] text-gray-700 hover:bg-white hover:shadow-sm rounded-lg transition-all"
              >
                💬 Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
